// How a field's rows decompose, for the "what did SignalCue add?" view.
//
// Three parts, always summing to `total`:
//
//   source   the ATS export already carried the value
//   added    SignalCue produced it — derived from your other fields, OR discovered
//            externally. Both are "the platform helped"; the split survives in
//            `derived`/`enriched` for tooltips.
//   missing  no value. `actionable` and `exhausted` survive on the row for tooltips
//            ("N of these can still be filled"), but they are not their own segments:
//            three colours is what a reader can hold at a glance.
//
// filled = source + added.
//
// TWO SOURCES, DELIBERATELY MERGED. `coverage` (lineage-attributed) knows the
// provenance split but only tracks the fields the enrichment ratchet targets.
// `dataquality` knows fill rates for more fields but nothing about where a value came
// from. We take the union:
//
//   - a field in coverage → use coverage's counts verbatim.
//   - a field only in dataquality → all its fills are `source`. That is NOT a guess:
//     the backend's own bucketOf() falls through to provSource for any field with no
//     lineage attribution (coverage/provenance.go).

export const SEGMENTS = ['source', 'added', 'missing'];

// Fields that exist for the platform's benefit, not the user's. global_company_id is
// an internal join key to the cross-tenant registry; nobody reading a data-quality
// page wants a coverage number for it.
export const HIDDEN_FIELDS = new Set(['global_company_id']);

// Fields SignalCue does NOT enrich by design — foundational IDENTITY (name), the first
// thing known about an entity, or SENSITIVE contact data (email/phone) we deliberately
// never source externally. Their unfilled cells are owned by the source ATS: not a
// fillable "Missing" gap and not a "can't enrich" dead-end, but a band of their own.
// legal_name is source-owned for its no-source remainder only (its ingest legal-suffix
// split fills stay attributed as `added`). MIRROR of coverage.IsSourceOwned
// (services/data-pipeline/internal/coverage/contract.go) — keep in sync.
export const SOURCE_OWNED_FIELDS = new Set([
  'companies.name',
  'companies.legal_name',
  'people.first_name',
  'people.full_name',
  'people.email',
  'people.phone'
]);

// entityEnrichable comes from the backend (TableProfile.enrichable, itself
// coverage.IsEnrichableEntity). When it is false the entity has no fillable gap at
// all: nothing outside the ATS knows who logged a call or what fee was agreed, so
// every unfilled cell is source-owned and the bar says "not enriched" instead of
// offering a fill we cannot perform. Defaults to true so an older payload without
// the flag behaves exactly as before.
export function isSourceOwned(entity, field, entityEnrichable = true) {
  if (entityEnrichable === false) return true;
  return SOURCE_OWNED_FIELDS.has(`${entity}.${field}`);
}

const num = (v) => Math.max(0, Number(v) || 0);

// fromCoverage folds one coverage row into the three segments.
function fromCoverage(entity, row, entityEnrichable) {
  const total = num(row.total);
  const source = num(row.source);
  const added = num(row.derived) + num(row.enriched);
  const missing = Math.max(0, total - source - added);
  return {
    field: row.field,
    total,
    source,
    added,
    derived: num(row.derived),
    enriched: num(row.enriched),
    // Kept for the tooltip, NOT drawn as segments.
    exhausted: num(row.exhausted),
    actionable: num(row.actionable),
    missing,
    // The gap of a source-owned field is owned by the ATS, not a fillable gap.
    source_owned: isSourceOwned(entity, row.field, entityEnrichable) ? missing : 0,
    filled: source + added,
    tracked: true,
    state: row.state
  };
}

// fromComparison folds one dataquality field (no provenance) into the same shape.
// enrich_valid_pct is "has a value" — Phase 1 treats valid = present.
function fromComparison(entity, cmp, rowCount, entityEnrichable) {
  const total = num(rowCount);
  const filled = Math.min(total, Math.round((num(cmp.enrich_valid_pct) / 100) * total));
  const missing = total - filled;
  return {
    field: cmp.field_pair_name,
    total,
    source: filled,
    added: 0,
    derived: 0,
    enriched: 0,
    exhausted: 0,
    actionable: 0,
    missing,
    source_owned: isSourceOwned(entity, cmp.field_pair_name, entityEnrichable) ? missing : 0,
    filled,
    tracked: false,
    state: null
  };
}

// fieldRows merges both contracts for one entity, dropping internal fields. Coverage
// wins where both know a field; coverage-only fields (people.first_name) are kept.
export function fieldRows(entity, comparisons, coverage, rowCount, entityEnrichable = true) {
  const cov = (coverage || []).filter((r) => r.entity === entity && !HIDDEN_FIELDS.has(r.field));
  const covByField = new Map(cov.map((r) => [r.field, r]));
  const out = [];
  const seen = new Set();

  (comparisons || []).forEach((cmp) => {
    const name = cmp.field_pair_name;
    if (!name || seen.has(name) || HIDDEN_FIELDS.has(name)) return;
    seen.add(name);
    const c = covByField.get(name);
    out.push(c ? fromCoverage(entity, c, entityEnrichable) : fromComparison(entity, cmp, rowCount, entityEnrichable));
  });

  cov.forEach((r) => {
    if (seen.has(r.field)) return;
    seen.add(r.field);
    out.push(fromCoverage(entity, r, entityEnrichable));
  });

  return out;
}

// segmentTotals sums an entity's fields. The denominator is rows × fields — the
// number of individual values the bar covers.
export function segmentTotals(fields) {
  const t = { source: 0, added: 0, missing: 0, derived: 0, enriched: 0, exhausted: 0, actionable: 0, source_owned: 0, checked: 0 };
  (fields || []).forEach((f) => {
    SEGMENTS.forEach((s) => {
      t[s] += f[s] || 0;
    });
    t.derived += f.derived || 0;
    t.enriched += f.enriched || 0;
    t.exhausted += f.exhausted || 0;
    t.actionable += f.actionable || 0;
    t.source_owned += f.source_owned || 0;
    t.checked += f.total || 0;
  });
  t.filled = t.source + t.added;
  t.fieldCount = (fields || []).length;
  return t;
}

// completeness is the "% complete" score, EXCLUDING the source-owned gap from the
// denominator: fields we don't enrich (name/email/phone) must not drag the score down.
// Their filled cells still count in the numerator; only their unfilled gap is removed.
export function completeness(totals) {
  return pctOf(totals.filled, num(totals.checked) - num(totals.source_owned));
}

// uplift: what the ATS gave (before) versus what we hold now (after).
//
// A field the ATS never carried has no multiplier — "×∞" is not a number. It is new.
// A field SignalCue did not touch is ×1, which should never be dressed up as a win.
export function uplift(field) {
  const before = num(field.source);
  const after = num(field.filled);
  if (before === 0) {
    return { before, after, factor: null, isNew: after > 0 };
  }
  return { before, after, factor: after / before, isNew: false };
}

// health mirrors qualityStatus() in internal/dataquality/metrics.go, which in turn
// mirrors data_quality_rules.json quality_thresholds. Keep the numbers in step:
// good ≥90% valid and ≤5% invalid; watch ≥70% and ≤15%; else poor; unknown with no
// fields.
//
// invalidPct is structurally 0 today — nothing is format-checked yet ("Phase 1 treats
// valid = present"). It is a parameter rather than a constant so this function needs
// no edit when validators ship.
export function health(validPct, invalidPct, fieldCount) {
  if (!fieldCount) return 'unknown';
  if (validPct >= 90 && invalidPct <= 5) return 'good';
  if (validPct >= 70 && invalidPct <= 15) return 'watch';
  return 'poor';
}

// healthOf rates an entity from its own composition: valid = filled, invalid = 0.
export function healthOf(totals) {
  return health(pctOf(totals.filled, totals.checked), 0, totals.fieldCount);
}

// change is the trend badge, in the vocabulary of IMPROVEMENT_STATUS.
//
// It is computed from OUR before → after (source → filled), NOT from the profiler's
// improvement_status. That value compares the raw ATS column against the canonical
// one, and for country/city the raw column is `company_location` — an address blob —
// so it reports `country 20.00% → 11.37%` as "worsened" when we in fact extracted a
// country from 1,546 of 2,657 addresses. Feeding a known-wrong number into a badge
// only launders it.
//
// The cost of being honest: source → filled is monotonic, so this can never return
// "worsened". Values dropped by validation (website: 8,500 raw → 8,412 stored) stay
// invisible until the created/rejected/extracted reframe lands.
export function change(field) {
  if (!num(field.filled)) return 'not_comparable';
  return num(field.added) > 0 ? 'improved' : 'unchanged';
}

// pctOf renders a segment as a share of the whole, guarding total = 0.
export function pctOf(value, total) {
  if (!total) return 0;
  return (100 * num(value)) / total;
}

// totalActionable sums the still-fillable backlog across every user-visible field —
// the headline "gaps SignalCue can fill". Hidden internal fields (global_company_id)
// are excluded so the number matches what the Quality view shows.
export function totalActionable(coverage) {
  return (coverage || [])
    .filter((r) => !HIDDEN_FIELDS.has(r.field))
    .reduce((n, r) => n + num(r.actionable), 0);
}

// actionableByEntity breaks that backlog down per record type, for a one-line preview
// of what an enrich run would work on. Returns [{entity, actionable}] descending,
// dropping entities with nothing to do.
export function actionableByEntity(coverage) {
  const by = {};
  (coverage || []).forEach((r) => {
    if (HIDDEN_FIELDS.has(r.field)) return;
    by[r.entity] = (by[r.entity] || 0) + num(r.actionable);
  });
  return Object.entries(by)
    .map(([entity, actionable]) => ({ entity, actionable }))
    .filter((e) => e.actionable > 0)
    .sort((a, b) => b.actionable - a.actionable);
}
